<template>
  <div class="community-guidelines-page">
    <div class="header">
      <div class="container">
        <div class="row">
          <div class="col-6 col">
            <h1 class="h2">
              Welcome to the {{ $config.platformName }} Community
            </h1>
            <p class="body-1">
              {{ $c.text('pages.community-guidelines.hero.description') }}
            </p>
          </div>
        </div>
      </div>
    </div>
    <div class="container">
      <h2 class="community-rules h2">
        {{ $config.platformName }} {{ page.content }}
      </h2>
      <div class="rules-wrap">
        <h3 class="community-accepted-rules h3">
          {{ $c.text('pages.community-guidelines.content.section1') }}
        </h3>
        <h4>
          <CheckMark />
          Be respectful.
        </h4>
        <p class="body-2">
          Exercise empathy and treat others the way you would like to be treated. Don’t harass other users or engage in hateful behavior (e.g., sexism, racism, homophobia, etc.). Foster a positive, welcoming environment for all.
        </p>
        <h4>
          <CheckMark />
          {{ $c.text('pages.community-guidelines.content.section1.title2') }}
        </h4>
        <p class="body-2">
          {{ $c.text('pages.community-guidelines.content.section1.description2') }}
        </p>
        <h4>
          <CheckMark />
          {{ $c.text('pages.community-guidelines.content.section1.title3') }}
        </h4>
        <p class="body-2">
          {{ $c.text('pages.community-guidelines.content.section1.description3') }}
        </p>
        <h4>
          <CheckMark />
          {{ $c.text('pages.community-guidelines.content.section1.title4') }}
        </h4>
        <p class="body-2">
          {{ $c.text('pages.community-guidelines.content.section1.description4') }}
          <a :href="`${$config.siteUrl}/question/how-to-use-the-qa-hub/`"><u>how to make the best of the Q&A Hub.</u></a>
        </p>
      </div>
      <div class="rules-wrap">
        <h3 class="community-rejected-rules h3">
          {{ $c.text('pages.community-guidelines.content.section2') }}
        </h3>
        <h4>
          <NoCheckMark class="forbid" />
          {{ $c.text('pages.community-guidelines.content.section2.title1') }}
        </h4>
        <p class="body-2">
          {{ $c.text('pages.community-guidelines.content.section2.description1') }}
        </p>
        <h4>
          <NoCheckMark class="forbid" />
          {{ $c.text('pages.community-guidelines.content.section2.title2') }}
        </h4>
        <p class="body-2">
          {{ $c.text('pages.community-guidelines.content.section2.description2') }}
        </p>
        <h4>
          <NoCheckMark class="forbid" />
          {{ $c.text('pages.community-guidelines.content.section2.title3') }}
        </h4>
        <p class="body-2">
          {{ $c.text('pages.community-guidelines.content.section2.description3') }}
        </p>
        <h4>
          <NoCheckMark class="forbid" />
          {{ $c.text('pages.community-guidelines.content.section2.title4') }}
        </h4>
        <p class="body-2">
          {{ $c.text('pages.community-guidelines.content.section2.description4') }}
        </p>
        <div class="final-words">
          <p class="body-2">
            We hope we can nourish an open, kind, and inclusive community together.
          </p>
          <p class="body-2">
            Thank you for being part of it!
          </p>
          <p class="body-2">
            The 365 Team
          </p>
        </div>
      </div>
      <ButtonArrow
        type="nuxt-link" text="Back to Q&A" to="/qa-hub/"
        class="back-to-qa"
      />
    </div>
  </div>
</template>

<script>
import global from '@itbpbg/lms-components/src/mixins/global';
import ButtonArrow from '@itbpbg/lms-components/src/components/ButtonArrow';
import CheckMark from '../assets/svg/checkmark.svg?vue-component';
import NoCheckMark from '../assets/svg/nocheckmark.svg?vue-component';

export default {
  components: {
    CheckMark,
    NoCheckMark,
    ButtonArrow,
  },
  mixins: [global],
  async asyncData({ $axios, route, $config }) {
    const page = await $axios.$get(`/pages${route.fullPath}`);
    page.fullPath = `${$config.siteUrl}${route.fullPath}`;
    page.metaFullImageUrl = `${$config.siteUrl}/resources/${page.metaImage}`;
    page.created = page.created || new Date();
    page.updated = page.updated || new Date();
    return {
      page,
      metaTitle: `${page.metaTitle}${+route.query.page > 1 ? ` - Page ${route.query.page}` : ''}`,
    };
  },
  head() {
    const linkedData = this.$linkedDataLoad([
      { key: 'WebSite' },
      { key: 'WebPage', values: this.page },
    ]);

    return {
      titleTemplate: `${this.page.metaTitle}%s`,
      link: [
        { rel: 'canonical', href: this.$route.fullPath },
      ],
      meta: this.metaProps(this.page),
      script: [
        {
          vmid: 'jsonld',
          type: 'application/ld+json',
          json: [linkedData],
        },
      ],
    };
  },
};
</script>

<style scoped lang="scss">
.community-guidelines-page {
  padding-top: 80px;
  position: relative;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    height: 80px;
    width: 100%;
    background-color: var(--main-dark);
  }
}

.header {
  height: 650px;
  background-color: var(--main-dark);
  color: var(--main-light);
  position: relative;
  z-index: 1;
  overflow: hidden;
  display: flex;
  align-items: center;

  &:before {
    content: '';
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    background-image: url('../assets/images/guidelines-wall.jpg');
    image-rendering: -webkit-optimize-contrast;
    background-repeat: no-repeat;
    background-size: contain;
    z-index: -1;
    left: unset;
    background-position: right;
  }

  .container {
    .h2 {
      margin-bottom: var(--spacing-36);
    }

    .body-1 {
      color: var(--main-light-shade-4);
      margin: 0;
    }
  }
}

.container {
  .community-rules {
    margin-top: var(--spacing-100);
    margin-bottom: var(--spacing-36);
    text-align: center;
  }

  h3.community-accepted-rules,
  h3.community-rejected-rules {
    margin-bottom: var(--spacing-60);
  }

  h4 {
    margin-bottom: var(--spacing-24);

    svg {
      margin-right: var(--spacing-12);

      &.forbid path[fill] {
        fill: var(--main-red);
      }
    }
  }

  p:not(:last-of-type) {
    margin-bottom: var(--spacing-60);
  }

  p:last-of-type {
    margin-bottom: var(--spacing-100);
  }

  .final-words {
    margin-bottom: var(--spacing-100);

    p {
      margin-bottom: 0;
      margin-top: var(--spacing-24);
    }
  }

  .back-to-qa {
    font-weight: 600;
    margin-bottom: 98px;
  }
}

@include bp($bp-laptop) {
  .community-guidelines-page {
    .rules-wrap {
      h4 {
        font-size: 24px;
      }
    }
  }
}

@include bp($bp-mobile) {
  .community-guidelines-page {
    padding-top: 68px;

    &:before {
      height: 68px;
    }
  }

  .header {
    &:before {
      background-size: unset;
      left: 35%;
    }

    .container {
      padding-top: 0;
      height: 100%;
      display: flex;
      align-items: center;

      .col-6 {
        margin-top: 0;

        .h2 {
          font-size: 32px;
          margin-bottom: var(--spacing-16);
        }
      }
    }
  }

  .container {
    p:last-of-type {
      margin-bottom: var(--spacing-80);
    }

    .final-words {
      margin-bottom: var(--spacing-55);

      p:last-of-type {
        margin-bottom: 0;
      }
    }

    .back-to-qa {
      margin-bottom: var(--spacing-80);
    }
  }
}

@include bp($bp-phone) {
  .community-guidelines-page {
    padding-top: 0;

    &:before {
      height: 0;
    }
  }

  .header {
    height: 780px;
    position: relative;

    &:before {
      background-image: url('../assets/images/guidelines-mobile.jpg');
      image-rendering: -webkit-optimize-contrast;
      background-size: 100%;
      left: 0;
    }

    .container {
      position: absolute;
      height: auto;
      bottom: 76px;

      .col-6 {
        max-width: 100%;
      }
    }
  }

  .community-rules.h2 {
    font-size: 32px;
    text-align: left;
    margin-top: var(--spacing-80);
  }

  .rules-wrap h4 {
    font-size: 24px;
  }
}

.fa {
  @import '../assets/sass/fa/pages/community-guidelines.scss';
}
</style>
